import type { PaymentEdgesModel } from '@/features/project/projects/_models/payment-edge';
import type { PaymentGroupsModel } from '@/features/project/projects/_models/payment-group';
import { type AmountUnit, BigCalculator, formatValueByUnit } from '@/utils';
import type { z } from 'zod';

export type PaymentNodeData = {
  label: string | number | null;
  displayCode?: string;
  title: string | undefined;
  paymentCount?: number | null;
  overview?: string | null;
  /**国自らが支出する間接経費 */
  paymentIndirectCostTextList?: string[] | null;
};

type BaseCalculateAndFormatPaymentTotalType = {
  paymentEdges: z.infer<typeof PaymentEdgesModel>;
  paymentGroups: z.infer<typeof PaymentGroupsModel>;
  unit: AmountUnit;
  isDebugMode: boolean;
};
type CalculateAndFormatPaymentTotalType = (
  | {
      type: 'rootNodes';
      totalAmountOfIndirectCosts: number | null | undefined;
    }
  | {
      paymentGroup: z.infer<typeof PaymentGroupsModel>[number];
      type: 'initialNodes';
    }
) &
  BaseCalculateAndFormatPaymentTotalType;

export const calculateAndFormatPaymentTotal = (
  args: CalculateAndFormatPaymentTotalType,
): string | number | null => {
  const { paymentEdges, paymentGroups, type, unit, isDebugMode } = args;

  const initialAmount =
    args.type === 'rootNodes' &&
    typeof args.totalAmountOfIndirectCosts === 'number'
      ? args.totalAmountOfIndirectCosts
      : null;

  const { amount, hasNegativeAmount, hasAnyPaymentEdges } =
    paymentEdges.reduce<{
      amount: number | null;
      hasNegativeAmount: boolean;
      hasAnyPaymentEdges: boolean;
    }>(
      (previous, current) => {
        if (type === 'rootNodes' && !current.isConnectedToSourceRoot) {
          return previous;
        }
        if (
          type === 'initialNodes' &&
          current.sourceNodeId !== args.paymentGroup.id
        ) {
          return previous;
        }
        const toPaymentGroup = paymentGroups.find(
          (pg) => pg.id === current.targetNodeId,
        );
        const amount = toPaymentGroup?.totalAmount;
        if (typeof amount !== 'number') {
          return previous;
        }
        return {
          amount: BigCalculator.add(previous.amount ?? 0, amount),
          hasNegativeAmount:
            previous.hasNegativeAmount ||
            !!toPaymentGroup?.negativeTotalAmountCount,
          hasAnyPaymentEdges: true,
        };
      },
      {
        amount: initialAmount,
        hasNegativeAmount: false,
        hasAnyPaymentEdges: false,
      },
    );

  // 支出先が無く、かつ初期金額が0またはnullの場合はnullを返す
  if (
    amount === null ||
    (amount === 0 && !hasAnyPaymentEdges && type === 'rootNodes')
  ) {
    return null;
  }

  return formatValueByUnit(amount, {
    operation: 'round',
    unit,
    negativeCount: hasNegativeAmount ? 1 : undefined,
    isDebugMode,
    // 正負相殺で合計が0になったフォールバック表示（--）には単位を付けない
    showUnitLabel: !(hasNegativeAmount && amount === 0),
  });
};
